import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { ToastContext, useModal } from 'cat-ecommerce-alloy';
import { useBreakpoint } from '@app/hooks';
import cx from 'classnames';
import { useFormikContext } from 'formik';
import ProductForm from '@app/components/common/ProductForm';
import QuickOrderActionButtons from './QuickOrderActionButtons';
import ShoppingCartLoader from '../ShoppingCartLoader';
import AddToCartModifications from './AddToCartModifications';
import {
  PAGE_BREAKPOINTS,
  STATUS,
  AFFILIATION_DEALER,
  LONG_TOAST_DELAY,
  JAPANESE_LANGUAGE_CODE
} from '@app/constants/commonConstants';
import {
  RESET_REPLACED_PARTS_INFO,
  RESET_REPLACED_PARTS_INFO_STATUS,
  SET_TOTAL_ITEMS_ADDED_TO_CART
} from '@app/store/orders/constants';
import {
  itemNumberValidator,
  quantityValidator,
  removeQuickOrderCookie,
  notesValidator,
  getNotesErrorMessageBasedOnField
} from './utils';
import { getSessionStorage, replaceTokensInString } from '@app/utils';
import { useQuickOrderCookie } from './hooks';
import { getDealerPriceAndAvailabilityReplacementWithReplacement } from '@app/store/checkout/actions';
import { getCookie } from '@app/utils';
import { SIS_CART_PARTS_LIST } from '@app/constants/cookieContants';
import {
  ADD_TO_CART_MODIFICATIONS_RESET,
  INITIAL_CART_ITEMS
} from '@app/store/checkout/constants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import { setError } from '@app/store/exception/actions';
import styles from './styles.module.scss';

const QuickOrder = ({
  errorInfo,
  initialItems,
  shoppingCartMaxItems,
  isAvailabilityToggled,
  setIsAvailabilityToggled,
  onAddToCartClick,
  openQOMobileModal
}) => {
  const dispatch = useDispatch();
  const { setTouched, values } = useFormikContext();
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const toastCtx = useContext(ToastContext);
  //TODO: properly memoize useModal in Alloy
  const modalProps = useModal();
  const quickOrderMobileModalOptions = useModal();
  const isFromQuickOrder = useSelector(s => s.checkout?.isFromQuickOrder);
  const shoppingPreferenceDataStatus = useSelector(
    s => s.shoppingPreferences.shoppingPreferenceDataStatus
  );
  const displayQuickOrder = useSelector(
    s => s.shoppingPreferences.shoppingPreferencesData.displayQuickOrder
  );
  const isInvalidParts = useSelector(state => state.checkout?.isInvalidParts);

  const userAffiliation = useSelector(s => s.common.userAffiliation);
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;

  const totalItemsAddedToCart = useSelector(
    s => s.orders.totalItemsAddedToCart
  );
  const replacedParts = useSelector(s => s.orders.replacedParts);
  const replacementHasReplacement = !!replacedParts?.length;
  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );
  const isPriceLoadComplete = useSelector(s => s.orders.isPriceLoadComplete);
  const dealerPriceAndAvailabilityStatus = useSelector(
    s => s.checkout.dealerPriceAndAvailabilityStatus
  );
  const isDefaultLanguageJapanese = useSelector(
    s => s.dealer.defaultLanguage === JAPANESE_LANGUAGE_CODE
  );
  const current = useSelector(s => s.orders.current);
  const orderInformation = useSelector(s => s.orders?.byId?.[current] || {});
  const isPCC_ShoppingCartSingleByteMessageJapan = useSelector(
    s => s.featureFlag?.PCC_ShoppingCartSingleByteMessageJapan
  );
  const isPCC_SpecialCharactersValidationFlag = useSelector(
    s => s.featureFlag?.PCC_SpecialCharactersValidationFlag
  );
  const isDealerPriceAndAvailabilityResolved =
    dealerPriceAndAvailabilityStatus === STATUS.RESOLVED;
  const showQuickOrder = displayQuickOrder || isDealerUser;
  const itemsRef = useRef(values.items);
  const loadAddToCartModification = isFromQuickOrder || !isInvalidParts;
  const sisCookie = getCookie(SIS_CART_PARTS_LIST);
  const singleByteRequiredErrorMessage = t('ERROR_SINGLE_BYTE_REQUIRED');

  /**
   * sets the form to touched when items changes so any max quantity errors can be shown
   */
  useEffect(() => {
    const touchedItems = itemsRef.current?.map(item => ({
      ...item,
      itemNumber: true,
      quantity: true
    }));
    setTouched({ items: touchedItems });
  }, [setTouched, itemsRef]);
  useQuickOrderCookie(openQOMobileModal, showQuickOrder);
  const shoppingPreferencesLoading =
    shoppingPreferenceDataStatus === STATUS.PENDING ||
    shoppingPreferenceDataStatus === STATUS.IDLE;

  const qOItemNumberValidator = props => {
    const params = {
      ...props,
      requiredErrorMessage: t('QUICKORDER_BLANK_ITEM'),
      invalidCharacterErrorMessage: isPCC_SpecialCharactersValidationFlag
        ? t('INVALID_ITEM_NUMBER')
        : t('INVALID_PART_NUMBER'),
      validateDoubleByte:
        isDefaultLanguageJapanese && isPCC_ShoppingCartSingleByteMessageJapan,
      specialCharacterValidation: isPCC_SpecialCharactersValidationFlag,
      doubleByteErrorMessage: singleByteRequiredErrorMessage
    };
    return itemNumberValidator(params);
  };

  const handleNoteFieldsValidator = props => {
    const params = {
      ...props,
      validateDoubleByte:
        isDefaultLanguageJapanese && isPCC_ShoppingCartSingleByteMessageJapan,
      doubleByteErrorMessage: singleByteRequiredErrorMessage,
      specialCharacterValidation: isPCC_SpecialCharactersValidationFlag,
      errorMessage: t(getNotesErrorMessageBasedOnField(props))
    };
    return notesValidator(params);
  };

  const handleQuantityValidator = props => {
    const params = {
      ...props,
      validateDoubleByte:
        isDefaultLanguageJapanese && isPCC_ShoppingCartSingleByteMessageJapan,
      doubleByteErrorMessage: singleByteRequiredErrorMessage
    };
    return quantityValidator(params);
  };

  const mobileModalOptionsRef = useRef(quickOrderMobileModalOptions);
  useEffect(() => {
    mobileModalOptionsRef.current = quickOrderMobileModalOptions;
  }, [quickOrderMobileModalOptions]);

  //To handle response from price-availability and trigger 2nd P&A call in case there are replacement(s) for replacement(s)
  useEffect(() => {
    if (!isOrderPriceAvailabilityLoading && isPriceLoadComplete) {
      dispatch({ type: RESET_REPLACED_PARTS_INFO_STATUS });
      const totalItemsAddedWithReplacement =
        replacedParts?.reduce(
          (subTotal, p) => subTotal + parseInt(p.quantity) || 0,
          0
        ) ?? 0;
      const itemsActuallyAddedToCart =
        totalItemsAddedToCart - totalItemsAddedWithReplacement;
      // Do not show parts added with replacements in the toast, since they'll be deleted
      if (itemsActuallyAddedToCart > 0) {
        toastCtx.createSuccessToast(
          replaceTokensInString(
            t('CART_ADD_SUCCESS_TOAST'),
            itemsActuallyAddedToCart
          ),
          LONG_TOAST_DELAY
        );
      }
      dispatch({ type: SET_TOTAL_ITEMS_ADDED_TO_CART });
      if (isDealerPriceAndAvailabilityResolved) {
        if (replacementHasReplacement) {
          // There are replacements for parts just added, so we delete them and try to add them again
          // (This will trigger the add to cart alternate flows)
          mobileModalOptionsRef.current.open();
          dispatch(getDealerPriceAndAvailabilityReplacementWithReplacement());
        } else {
          // There are no more replacements of replacements, items are finally in the cart
          mobileModalOptionsRef.current.close();
          dispatch({ type: ADD_TO_CART_MODIFICATIONS_RESET });
          dispatch({ type: RESET_REPLACED_PARTS_INFO });
          const initialOrderItems = getSessionStorage(INITIAL_CART_ITEMS);
          const { orderItem } = orderInformation;
          const totalQuantity = orderItem?.reduce((total, item) => {
            return total + +item.quantity;
          }, 0);
          if (initialOrderItems == totalQuantity) {
            dispatch(
              setError(ERROR_DOMAIN.CART, ERROR_PATH.SIS_TO_CART_IMPORT, {
                title: t('NO_PARTS_SIS_HEADER'),
                message: t('WHAT_WOULD_YOU_LIKE_TO_DO')
              })
            );
          }
        }
      }
    }
  }, [
    isDealerPriceAndAvailabilityResolved,
    isOrderPriceAvailabilityLoading,
    isPriceLoadComplete,
    replacedParts,
    replacementHasReplacement,
    totalItemsAddedToCart,
    toastCtx,
    t,
    dispatch,
    orderInformation
  ]);

  const getContent = () => {
    if (!showQuickOrder && !sisCookie) {
      return null;
    }
    return (
      isMD && (
        <>
          {showQuickOrder && (
            <ProductForm
              actionButtons={
                <QuickOrderActionButtons
                  showAddToCartCheckout
                  onAddToCartClick={onAddToCartClick}
                />
              }
              clearButtonClassName="px-0"
              errorInfo={errorInfo}
              formContentClassName="w-100 flex-wrap"
              initialItems={initialItems}
              itemNumberValidator={qOItemNumberValidator}
              maxRows={shoppingCartMaxItems}
              onClearAll={removeQuickOrderCookie}
              productFormTitle={t('Quick_Title')}
              quantityValidator={handleQuantityValidator}
              noteFieldsValidator={handleNoteFieldsValidator}
              shoppingCartMaxItems={shoppingCartMaxItems}
              withCopyPastePopup
              withFileUpload
              shoppingPreferencesLoaded={!shoppingPreferencesLoading}
            />
          )}
          {loadAddToCartModification && (
            <AddToCartModifications
              modalOptions={modalProps}
              isAvailabilityToggled={isAvailabilityToggled}
              setIsAvailabilityToggled={setIsAvailabilityToggled}
            />
          )}
        </>
      )
    );
  };
  return shoppingPreferencesLoading ? (
    <ShoppingCartLoader />
  ) : (
    <section
      className={cx('d-print-none', styles['quick-order-content__container'])}
    >
      {getContent()}
    </section>
  );
};

QuickOrder.propTypes = {
  errorInfo: PropTypes.shape({
    domain: PropTypes.string,
    message: PropTypes.string,
    path: PropTypes.string
  }),
  initialItems: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.string,
      itemNumber: PropTypes.string,
      assetId: PropTypes.string,
      lineItemNote: PropTypes.string,
      customerPartNumber: PropTypes.string,
      customerItemNumber: PropTypes.string,
      id: PropTypes.string
    })
  ),
  shoppingCartMaxItems: PropTypes.number,
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  onAddToCartClick: PropTypes.func,
  openQOMobileModal: PropTypes.func
};
export default QuickOrder;
